import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Trash2, 
  Edit2, 
  Loader2,
  X,
  Calendar,
  DollarSign,
  CheckCircle2,
  Clock,
  ArrowRight,
  FileText,
  Users,
  ChevronRight, 
  Download,
  CreditCard,
  Banknote
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDocs,
  getDoc } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import { ensureAccountByCode, getAccountById } from '../services/accountingService';
import { cn } from '../lib/utils';
import { getDefaultCurrency } from '../lib/currency';
import ConfirmModal from './ConfirmModal';

interface PayrollSettings {
  companyName: string;
  taxNumber: string;
  payFrequency: 'Monthly' | 'Weekly' | 'Bi-weekly';
  payDay: number;
  currency: string;
  salaryAccount?: string;
  liabilityAccount?: string;
  taxAccount?: string;
  deductions: { name: string; percentage: number }[];
  allowances: { name: string; percentage: number }[];
}

interface PayRunItem {
  employeeId: string;
  employeeName: string;
  grossSalary: number;
  deductions: { name: string; amount: number }[];
  allowances: { name: string; amount: number }[];
  totalDeductions: number;
  totalAllowances: number;
  netSalary: number;
}

interface PayRun {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  status: 'Draft' | 'Approved' | 'Paid';
  totalGross: number;
  totalNet: number;
  employeeCount: number;
  items: PayRunItem[];
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
  journalPosted?: boolean;
}

interface EditingField {
  employeeId: string;
  type: 'salary' | 'allowance' | 'deduction';
  index?: number;
}

type ApiPayRun = Record<string, unknown>;

const asApiPayRun = (value: unknown): ApiPayRun =>
  value && typeof value === 'object' ? value as ApiPayRun : {};

const normalizePayRunLineItems = (value: unknown): { name: string; amount: number }[] =>
  Array.isArray(value)
    ? value.map(line => {
        const item = asApiPayRun(line);
        return {
          name: String(item.name ?? ''),
          amount: Number(item.amount ?? 0),
        };
      })
    : [];

const normalizePayRunItem = (value: unknown): PayRunItem => {
  const item = asApiPayRun(value);
  const deductions = normalizePayRunLineItems(item.deductions);
  const allowances = normalizePayRunLineItems(item.allowances);
  const grossSalary = Number(item.grossSalary ?? item.gross_salary ?? 0);
  const totalDeductions = Number(item.totalDeductions ?? item.total_deductions ?? deductions.reduce((sum, deduction) => sum + deduction.amount, 0));
  const totalAllowances = Number(item.totalAllowances ?? item.total_allowances ?? allowances.reduce((sum, allowance) => sum + allowance.amount, 0));

  return {
    employeeId: String(item.employeeId ?? item.employee_id ?? ''),
    employeeName: String(item.employeeName ?? item.employee_name ?? ''),
    grossSalary,
    deductions,
    allowances,
    totalDeductions,
    totalAllowances,
    netSalary: Number(item.netSalary ?? item.net_salary ?? grossSalary + totalAllowances - totalDeductions),
  };
};

const normalizePayRun = (value: unknown): PayRun => {
  const payRun = asApiPayRun(value);
  const itemData = payRun.items ?? payRun.payrun_items;
  const items = Array.isArray(itemData) ? itemData.map(normalizePayRunItem) : [];
  const status = String(payRun.status ?? 'Draft').toLowerCase();
  const createdBy = payRun.createdBy ?? payRun.created_by;

  return {
    id: String(payRun.id ?? payRun.payrun_id ?? ''),
    name: String(payRun.name ?? ''),
    periodStart: String(payRun.periodStart ?? payRun.period_start ?? ''),
    periodEnd: String(payRun.periodEnd ?? payRun.period_end ?? ''),
    paymentDate: String(payRun.paymentDate ?? payRun.payment_date ?? ''),
    status: status === 'approved' ? 'Approved' : status === 'paid' ? 'Paid' : 'Draft',
    totalGross: Number(payRun.totalGross ?? payRun.total_gross ?? 0),
    totalNet: Number(payRun.totalNet ?? payRun.total_net ?? 0),
    employeeCount: Number(payRun.employeeCount ?? payRun.employee_count ?? items.length),
    items,
    createdAt: payRun.createdAt ?? payRun.created_at,
    updatedAt: payRun.updatedAt ?? payRun.updated_at,
    createdBy: typeof createdBy === 'string' ? createdBy : undefined,
    journalPosted: Boolean(payRun.journalPosted ?? payRun.journal_posted ?? payRun.alreadyPosted ?? payRun.already_posted),
  };
};

const getLocalPayRuns = (payload: unknown): PayRun[] => {
  if (Array.isArray(payload)) return payload.map(normalizePayRun);

  const response = asApiPayRun(payload);
  const payRuns = response.payruns ?? response.data;
  return Array.isArray(payRuns) ? payRuns.map(normalizePayRun) : [];
};

const getLocalPayRun = (payload: unknown): PayRun => {
  const response = asApiPayRun(payload);
  return normalizePayRun(response.payrun ?? response.data ?? payload);
};

export default function PayRunView({ orgId, orgSettings, language = 'en' }: { orgId: string, orgSettings?: any, language?: string }) {
  const [payRuns, setPayRuns] = useState<PayRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    date: new Date().toISOString().split('T')[0],
    accountId: '',
    method: 'Bank Transfer',
    reference: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPayRun, setSelectedPayRun] = useState<PayRun | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<any[]>([]);
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettings | null>(null);
  const [editingField, setEditingField] = useState<EditingField | null>(null);

  const effectiveCurrency = payrollSettings?.currency || getDefaultCurrency(orgSettings);

  const [newPayRun, setNewPayRun] = useState<Omit<PayRun, 'id'>>({
    name: '',
    periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    periodEnd: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    paymentDate: new Date().toISOString().split('T')[0],
    status: 'Draft',
    totalGross: 0,
    totalNet: 0,
    employeeCount: 0,
    items: []
  });

  useEffect(() => {
    if (!orgId) return;

    const localAuthToken = typeof window !== 'undefined'
      ? localStorage.getItem('app_auth_token')
      : null;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    if (localAuthToken) {
      const fetchLocalPayRuns = async () => {
        try {
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/payruns`, {
            headers: { Authorization: `Bearer ${localAuthToken}` },
          });
          if (!response.ok) throw new Error(`Failed to load payruns: ${response.status}`);

          const payload = await response.json();
          if (!cancelled) {
            setPayRuns(getLocalPayRuns(payload));
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error fetching payruns:", error);
          if (!cancelled) {
            notify.error("Failed to load payruns");
            setIsLoading(false);
          }
        }
      };

      fetchLocalPayRuns();
    } else {
      const q = query(
        collection(db, 'payruns'),
        where('orgId', '==', orgId),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        const payRunData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PayRun[];
        setPayRuns(payRunData);
        setIsLoading(false);
      }, (error) => {
        console.error("Error fetching payruns:", error);
        notify.error("Failed to load payruns");
        setIsLoading(false);
        handleFirestoreError(error, OperationType.LIST, 'payruns');
      });
    }

    // Fetch employees for payrun creation
    const fetchEmployees = async () => {
      try {
        const empSnapshot = await getDocs(query(
          collection(db, 'employees'), 
          where('orgId', '==', orgId),
          where('status', '==', 'Active')
        ));
        setEmployees(empSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching employees:", error);
        handleFirestoreError(error, OperationType.LIST, 'employees');
      }
    };

    // Fetch payroll settings
    const fetchPayrollSettings = async () => {
      try {
        const docRef = doc(db, 'organizations', orgId, 'settings', 'payroll');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPayrollSettings(docSnap.data() as PayrollSettings);
        }
      } catch (error) {
        console.error("Error fetching payroll settings:", error);
      }
    };

    // Fetch bank accounts and enabled chart of accounts for payment
    const fetchPaymentAccounts = async () => {
      try {
        const bankSnapshot = await getDocs(query(
          collection(db, 'bank_accounts'),
          where('orgId', '==', orgId)
        ));
        const bankAccs = bankSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), source: 'bank' }));
        
        const coaSnapshot = await getDocs(query(
          collection(db, 'chart_of_accounts'),
          where('orgId', '==', orgId),
          where('enablePayments', '==', true),
          where('status', '==', 'active')
        ));
        const coaAccs = coaSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), source: 'coa' }));
        
        setPaymentAccounts([...bankAccs, ...coaAccs]);
      } catch (error) {
        console.error("Error fetching payment accounts:", error);
        handleFirestoreError(error, OperationType.LIST, 'accounts');
      }
    };

    fetchEmployees();
    fetchPayrollSettings();
    fetchPaymentAccounts();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [orgId]);

  const calculatePayRunItems = () => {
    return employees.map(emp => {
      const grossSalary = emp.salary || 0;
      
      const employeeDeductions = (payrollSettings?.deductions || []).map(d => ({
        name: d.name,
        amount: (grossSalary * d.percentage) / 100
      }));
      
      const employeeAllowances = (payrollSettings?.allowances || []).map(a => ({
        name: a.name,
        amount: (grossSalary * a.percentage) / 100
      }));
      
      const totalDeductions = employeeDeductions.reduce((sum, d) => sum + d.amount, 0);
      const totalAllowances = employeeAllowances.reduce((sum, a) => sum + a.amount, 0);
      const netSalary = grossSalary + totalAllowances - totalDeductions;
      
      return {
        employeeId: emp.id,
        employeeName: String(emp.firstName ?? '') + ' ' + String(emp.lastName ?? ''),
        grossSalary,
        deductions: employeeDeductions,
        allowances: employeeAllowances,
        totalDeductions,
        totalAllowances,
        netSalary
      };
    });
  };

  const handleItemChange = (employeeId: string, field: 'salary' | 'allowance' | 'deduction', value: number, index?: number) => {
    const updatedItems = [...newPayRun.items];
    const itemIndex = updatedItems.findIndex(item => item.employeeId === employeeId);
    if (itemIndex === -1) return;

    const item = { ...updatedItems[itemIndex] };
    
    if (field === 'salary') {
      item.grossSalary = value;
    } else if (field === 'allowance' && typeof index === 'number') {
      const newAllowances = [...item.allowances];
      newAllowances[index] = { ...newAllowances[index], amount: value };
      item.allowances = newAllowances;
    } else if (field === 'deduction' && typeof index === 'number') {
      const newDeductions = [...item.deductions];
      newDeductions[index] = { ...newDeductions[index], amount: value };
      item.deductions = newDeductions;
    }

    // Recalculate item totals
    item.totalAllowances = item.allowances.reduce((sum, a) => sum + a.amount, 0);
    item.totalDeductions = item.deductions.reduce((sum, d) => sum + d.amount, 0);
    item.netSalary = item.grossSalary + item.totalAllowances - item.totalDeductions;

    updatedItems[itemIndex] = item;

    // Recalculate payrun totals
    const totalGross = updatedItems.reduce((sum, i) => sum + i.grossSalary, 0);
    const totalNet = updatedItems.reduce((sum, i) => sum + i.netSalary, 0);

    setNewPayRun({
      ...newPayRun,
      items: updatedItems,
      totalGross,
      totalNet
    });
  };

  const handleSavePayRun = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payRunData = {
        ...newPayRun,
        orgId,
        updatedAt: serverTimestamp()
      };

      if (isEditing && selectedPayRun) {
        const localAuthToken = typeof window !== 'undefined'
          ? localStorage.getItem('app_auth_token')
          : null;

        if (localAuthToken) {
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/payruns/${selectedPayRun.id}`, {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${localAuthToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(newPayRun),
          });
          if (!response.ok) throw new Error(`Failed to update payrun: ${response.status}`);

          const updatedPayRun = getLocalPayRun(await response.json());
          setPayRuns(currentPayRuns => currentPayRuns.map(payRun =>
            payRun.id === updatedPayRun.id ? updatedPayRun : payRun
          ));
          setSelectedPayRun(updatedPayRun);
        } else {
          await updateDoc(doc(db, 'payruns', selectedPayRun.id), payRunData);
        }
        notify.confirm("Payrun updated successfully");
      } else {
        const localAuthToken = typeof window !== 'undefined'
          ? localStorage.getItem('app_auth_token')
          : null;

        if (localAuthToken) {
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/payruns`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localAuthToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(newPayRun),
          });
          if (!response.ok) throw new Error(`Failed to create payrun: ${response.status}`);

          const createdPayRun = getLocalPayRun(await response.json());
          setPayRuns(currentPayRuns => [createdPayRun, ...currentPayRuns]);
        } else {
          await addDoc(collection(db, 'payruns'), {
            ...payRunData,
            createdBy: auth.currentUser?.uid,
            createdAt: serverTimestamp()
          });
        }
        notify.confirm("Payrun created successfully");
      }
      setIsModalOpen(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving payrun:", error);
      notify.error("Failed to save payrun");
      handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'payruns');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePayRun = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'payruns', id));
      notify.confirm("Payrun deleted successfully");
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting payrun:", error);
      notify.error("Failed to delete payrun");
      handleFirestoreError(error, OperationType.DELETE, 'payruns');
    }
  };

  const handleStatusUpdate = async (payRun: PayRun, newStatus: 'Approved' | 'Paid') => {
    try {
      const localAuthToken = typeof window !== 'undefined'
        ? localStorage.getItem('app_auth_token')
        : null;

      if (localAuthToken) {
        const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/payruns/${payRun.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${localAuthToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        });
        if (!response.ok) throw new Error(`Failed to update payrun status: ${response.status}`);

        let updatedPayRun = getLocalPayRun(await response.json());

        if (newStatus === 'Approved' && !updatedPayRun.journalPosted) {
          const journalResponse = await fetch(
            `/api/organizations/${encodeURIComponent(orgId)}/payruns/${updatedPayRun.id}/post-journal`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${localAuthToken}` },
            }
          );
          if (!journalResponse.ok) throw new Error(`Failed to post payroll journal: ${journalResponse.status}`);

          const journalPayload = asApiPayRun(await journalResponse.json());
          const alreadyPosted = journalPayload.alreadyPosted === true;
          updatedPayRun = { ...updatedPayRun, journalPosted: true };
          notify.confirm(alreadyPosted ? 'Payroll journal was already posted' : 'Payroll journal posted');
        }

        setPayRuns(currentPayRuns => currentPayRuns.map(currentPayRun =>
          currentPayRun.id === updatedPayRun.id ? updatedPayRun : currentPayRun
        ));
        setSelectedPayRun(currentPayRun =>
          currentPayRun?.id === updatedPayRun.id ? updatedPayRun : currentPayRun
        );
      } else {
        await updateDoc(doc(db, 'payruns', payRun.id), {
          status: newStatus,
          updatedAt: serverTimestamp()
        });

        if (newStatus === 'Approved') {
          // Per SYSTEM_DOCUMENTATION §8 (Approve & Post Pay Run):
          //   Debit  Salaries & Wages Expense = Total Gross
          //   Credit Tax Payable / Statutory Liabilities = Employee Tax Withheld
          //   Credit Payroll Payable = Total Net Salaries Payable
          const uid = auth.currentUser?.uid || 'system';
          const salaryAccount = (payrollSettings?.salaryAccount ? await getAccountById(payrollSettings.salaryAccount) : null)
            || await ensureAccountByCode(orgId, '6010', { name: 'Salaries and Wages', type: 'Expense', nature: 'Debit', taxRate: '0%' }, uid);
          const liabilityAccount = (payrollSettings?.liabilityAccount ? await getAccountById(payrollSettings.liabilityAccount) : null)
            || await ensureAccountByCode(orgId, '2030', { name: 'Payroll Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, uid);
          const taxWithheld = (payRun.items || []).reduce((sum, item) => sum + (item.totalDeductions || 0), 0);
          // PayRun.totalGross stores base salary and excludes allowances. Net pay plus
          // withheld deductions is the actual gross payroll expense being accrued.
          const grossPayrollExpense = (payRun.totalNet || 0) + taxWithheld;

          const journalItems: { accountId: string; description: string; debit: number; credit: number }[] = [
            {
              accountId: salaryAccount.id,
              description: 'Payroll Expense (Gross)',
              debit: grossPayrollExpense,
              credit: 0
            }
          ];

          if (taxWithheld > 0) {
            // Resolve (or auto-create) the statutory tax payable account (code 2200).
            const taxAccount = (payrollSettings?.taxAccount ? await getAccountById(payrollSettings.taxAccount) : null)
              || await ensureAccountByCode(orgId, '2200', { name: 'Payroll Tax Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, uid);
            journalItems.push({
              accountId: taxAccount.id,
              description: 'Employee Tax Withheld',
              debit: 0,
              credit: taxWithheld
            });
          }

          journalItems.push({
            accountId: liabilityAccount.id,
            description: 'Payroll Liability (Net Payable)',
            debit: 0,
            credit: payRun.totalNet
          });

          const journalTotalDebit = journalItems.reduce((sum, i) => sum + i.debit, 0);
          const journalTotalCredit = journalItems.reduce((sum, i) => sum + i.credit, 0);

          // Create manual journal to reflect payroll expense and liabilities
          await addDoc(collection(db, 'manual_journals'), {
            orgId,
            date: new Date().toISOString().split('T')[0],
            narration: `Payroll Run Approved: ${payRun.name}`,
            reference: `PR-${payRun.id?.slice(-6)?.toUpperCase() || 'PAY'}`,
            status: 'Posted',
            items: journalItems,
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser?.uid,
            totalDebit: journalTotalDebit,
            totalCredit: journalTotalCredit,
          });
        }
      }
      
      notify.confirm(`Payrun marked as ${newStatus.toLowerCase()}`);
    } catch (error) {
      console.error("Error updating payrun status:", error);
      notify.error("Failed to update status");
      handleFirestoreError(error, OperationType.UPDATE, 'payruns');
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedPayRun || isSaving || !paymentData.accountId) {
      if (!paymentData.accountId) notify.error("Please select a payment account");
      return;
    }
    setIsSaving(true);
    try {
      const localAuthToken = typeof window !== 'undefined'
        ? localStorage.getItem('app_auth_token')
        : null;

      if (localAuthToken) {
        const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/payruns/${selectedPayRun.id}/mark-paid`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuthToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(paymentData),
        });
        if (!response.ok) throw new Error(`Failed to mark payrun as paid: ${response.status}`);

        const updatedPayRun = getLocalPayRun(await response.json());
        setPayRuns(currentPayRuns => currentPayRuns.map(payRun =>
          payRun.id === updatedPayRun.id ? updatedPayRun : payRun
        ));
        setSelectedPayRun(updatedPayRun);
      } else {
        await updateDoc(doc(db, 'payruns', selectedPayRun.id), {
          status: 'Paid',
          paymentDate: paymentData.date,
          paymentAccount: paymentData.accountId,
          paymentMethod: paymentData.method,
          paymentReference: paymentData.reference,
          updatedAt: serverTimestamp()
        });

        // Create a transaction record
        await addDoc(collection(db, 'transactions'), {
          orgId,
          date: paymentData.date,
          description: `Payroll Payment: ${selectedPayRun.name}`,
          category: 'Payroll',
          amount: -selectedPayRun.totalNet,
          status: 'Completed',
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp(),
          paymentMethod: paymentData.method,
          reference: paymentData.reference,
          accountId: paymentData.accountId,
          payRunId: selectedPayRun.id
        });

        // Per SYSTEM_DOCUMENTATION §8 (Mark Pay Run Paid) post the salary disbursement:
        //   Debit  Payroll Payable = Total Net Salaries Paid
        //   Credit Bank Account    = Total Net Salaries Paid
        {
          const uid = auth.currentUser?.uid || 'system';
          const liabilityAccount = (payrollSettings?.liabilityAccount ? await getAccountById(payrollSettings.liabilityAccount) : null)
            || await ensureAccountByCode(orgId, '2030', { name: 'Payroll Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, uid);
          await addDoc(collection(db, 'manual_journals'), {
            orgId,
            date: paymentData.date,
            narration: `Payroll Disbursement: ${selectedPayRun.name}`,
            reference: `PR-${selectedPayRun.id?.slice(-6)?.toUpperCase() || 'PAY'}`,
            status: 'Posted',
            items: [
              {
                accountId: liabilityAccount.id,
                description: 'Payroll Liability Settled',
                debit: selectedPayRun.totalNet,
                credit: 0
              },
              {
                accountId: paymentData.accountId,
                description: 'Salary Disbursement',
                debit: 0,
                credit: selectedPayRun.totalNet
              }
            ],
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser?.uid,
            totalDebit: selectedPayRun.totalNet,
            totalCredit: selectedPayRun.totalNet,
          });
        }
      }

      setIsPaymentModalOpen(false);
      notify.confirm("Payrun marked as paid and transaction recorded");
    } catch (error) {
      console.error("Error marking payrun as paid:", error);
      notify.error("Failed to record payment");
      handleFirestoreError(error, OperationType.UPDATE, 'payruns');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPayRuns = payRuns.filter(pr => {
    const matchesSearch = pr.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || pr.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">PayRuns</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Process and manage employee payroll cycles</p>
        </div>
        <button 
          onClick={() => {
            setIsEditing(false);
            const initialItems = calculatePayRunItems();
            setNewPayRun({
              name: `Payroll - ${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}`,
              periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
              periodEnd: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
              paymentDate: new Date().toISOString().split('T')[0],
              status: 'Draft',
              totalGross: initialItems.reduce((sum, i) => sum + i.grossSalary, 0),
              totalNet: initialItems.reduce((sum, i) => sum + i.netSalary, 0),
              employeeCount: employees.length,
              items: initialItems
            });
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          <Plus className="w-4 h-4" />
          <span>New PayRun</span>
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/50 border border-[#141414] p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
            <p className="text-sm font-bold uppercase tracking-widest opacity-40">Draft PayRuns</p>
          </div>
          <p className="text-3xl font-bold italic serif">{payRuns.filter(p => p.status === 'Draft').length}</p>
        </div>
        <div className="bg-white/50 border border-[#141414] p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <p className="text-sm font-bold uppercase tracking-widest opacity-40">Approved</p>
          </div>
          <p className="text-3xl font-bold italic serif">{payRuns.filter(p => p.status === 'Approved').length}</p>
        </div>
        <div className="bg-white/50 border border-[#141414] p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center">
              <Banknote className="w-5 h-5" />
            </div>
            <p className="text-sm font-bold uppercase tracking-widest opacity-40">Paid This Month</p>
          </div>
          <p className="text-3xl font-bold italic serif">
            {effectiveCurrency} {payRuns.filter(p => p.status === 'Paid').reduce((sum, p) => sum + p.totalNet, 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
          {['All', 'Draft', 'Approved', 'Paid'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border",
                statusFilter === status 
                  ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                  : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
              )}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input 
            type="text" 
            placeholder="Search payruns..." 
            className="w-full pl-10 pr-4 py-2 bg-white/50 border border-[#141414]/10 rounded-full outline-none focus:border-[#141414] transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading payruns...</p>
        </div>
      ) : filteredPayRuns.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No payruns found</p>
          <p className="text-sm opacity-30 mt-2">Create your first payrun to process payroll</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPayRuns.map((pr) => (
            <div 
              key={pr.id}
              className="bg-white/50 border border-[#141414] rounded-3xl p-6 hover:shadow-xl transition-all group cursor-pointer"
              onClick={() => {
                setSelectedPayRun(pr);
                setNewPayRun({ ...pr });
                setIsEditing(true);
                setIsModalOpen(true);
              }}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl",
                    pr.status === 'Paid' ? "bg-green-100 text-green-600" :
                    pr.status === 'Approved' ? "bg-orange-100 text-orange-600" :
                    "bg-blue-100 text-blue-600"
                  )}>
                    <Banknote className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold tracking-tight group-hover:text-orange-600 transition-colors">{pr.name}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-xs opacity-50 font-mono">{new Date(pr.periodStart).toLocaleDateString()} - {new Date(pr.periodEnd).toLocaleDateString()}</p>
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-[10px] font-bold border",
                        pr.status === 'Paid' ? "bg-green-100 text-green-700 border-green-200" :
                        pr.status === 'Approved' ? "bg-orange-100 text-orange-700 border-orange-200" :
                        "bg-blue-100 text-blue-700 border-blue-200"
                      )}>
                        {pr.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <p className="text-[10px] uppercase font-bold opacity-40">Employees</p>
                    <div className="flex items-center justify-end gap-1">
                      <Users className="w-3 h-3 opacity-30" />
                      <p className="text-sm font-bold">{pr.employeeCount}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase font-bold opacity-40">Total Net</p>
                    <p className="text-sm font-bold">{effectiveCurrency} {pr.totalNet.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {pr.status === 'Draft' && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusUpdate(pr, 'Approved');
                        }}
                        className="bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full text-xs font-bold hover:opacity-90 transition-opacity"
                      >
                        Approve
                      </button>
                    )}
                    {pr.status === 'Approved' && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPayRun(pr);
                          setPaymentData(prev => ({ ...prev, date: pr.paymentDate || new Date().toISOString().split('T')[0] }));
                          setIsPaymentModalOpen(true);
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-full text-xs font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                      >
                        <CreditCard className="w-3 h-3" />
                        Mark as Paid
                      </button>
                    )}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(pr.id);
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-5 h-5 opacity-20 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit PayRun Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] w-full max-w-4xl rounded-[32px] overflow-hidden shadow-2xl border border-[#141414] flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-8 border-b border-[#141414] flex items-center justify-between bg-white/50">
              <div>
                <h2 className="text-2xl font-bold italic serif tracking-tight">
                  {isEditing ? 'PayRun Details' : 'Create New PayRun'}
                </h2>
                <p className="text-sm opacity-50 italic serif">Set the period and payment date for this payroll cycle</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSavePayRun} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Cycle Configuration</h3>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">PayRun Name *</label>
                    <input 
                      required
                      type="text" 
                      className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                      placeholder="e.g. March 2024 Payroll"
                      value={newPayRun.name}
                      onChange={(e) => setNewPayRun({...newPayRun, name: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Period Start *</label>
                      <input 
                        required
                        type="date" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                        value={newPayRun.periodStart}
                        onChange={(e) => setNewPayRun({...newPayRun, periodStart: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Period End *</label>
                      <input 
                        required
                        type="date" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                        value={newPayRun.periodEnd}
                        onChange={(e) => setNewPayRun({...newPayRun, periodEnd: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Date *</label>
                    <input 
                      required
                      type="date" 
                      className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                      value={newPayRun.paymentDate}
                      onChange={(e) => setNewPayRun({...newPayRun, paymentDate: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Summary Overview</h3>
                  
                  <div className="bg-white/50 border border-[#141414] p-6 rounded-3xl space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-bold uppercase tracking-widest opacity-40">Active Employees</p>
                      <p className="font-bold">{employees.length}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-bold uppercase tracking-widest opacity-40">Estimated Gross</p>
                      <p className="font-bold">{effectiveCurrency} {newPayRun.totalGross.toLocaleString()}</p>
                    </div>
                    <div className="pt-4 border-t border-[#141414]/10 flex justify-between items-center">
                      <p className="text-sm font-bold uppercase tracking-widest">Total Net Pay</p>
                      <p className="text-xl font-bold italic serif">{effectiveCurrency} {newPayRun.totalNet.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-2xl flex gap-3">
                    <Clock className="w-5 h-5 text-orange-600 shrink-0" />
                    <p className="text-xs text-orange-800 leading-relaxed">
                      This payrun will include all active employees. You can adjust individual line items after creating the draft.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Employee Breakdown</h3>
                <div className="space-y-4">
                  {newPayRun.items.map(item => (
                    <div key={item.employeeId} className="bg-white/50 border border-[#141414]/10 p-6 rounded-3xl space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#141414] text-[#E4E3E0] rounded-xl flex items-center justify-center text-sm font-bold">
                            {item.employeeName.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold">{item.employeeName}</p>
                            <div 
                              className="flex items-center gap-1 cursor-pointer group/edit"
                              onDoubleClick={() => newPayRun.status === 'Draft' && setEditingField({ employeeId: item.employeeId, type: 'salary' })}
                            >
                              {editingField?.employeeId === item.employeeId && editingField.type === 'salary' ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] opacity-50 uppercase font-bold tracking-widest">Gross: {effectiveCurrency}</span>
                                  <input 
                                    autoFocus
                                    type="number"
                                    className="w-20 bg-white border border-[#141414]/20 rounded px-1 py-0.5 text-[10px] font-bold"
                                    value={isNaN(item.grossSalary) ? '' : (item.grossSalary === 0 ? '' : item.grossSalary)}
                                    onChange={(e) => handleItemChange(item.employeeId, 'salary', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                    onBlur={() => setEditingField(null)}
                                    onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                                  />
                                </div>
                              ) : (
                                <>
                                  <p className="text-[10px] opacity-50 uppercase font-bold tracking-widest">Gross: {effectiveCurrency} {item.grossSalary.toLocaleString()}</p>
                                  {newPayRun.status === 'Draft' && <Edit2 className="w-2 h-2 opacity-0 group-hover/edit:opacity-30" />}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold italic serif">{effectiveCurrency} {item.netSalary.toLocaleString()}</p>
                          <p className="text-[10px] opacity-50 uppercase font-bold tracking-widest">Net Pay</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-8 pt-4 border-t border-[#141414]/5">
                        <div className="space-y-2">
                          <p className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Allowances</p>
                          {item.allowances.length > 0 ? (
                            <div className="space-y-1">
                              {item.allowances.map((a, idx) => (
                                <div 
                                  key={idx} 
                                  className="flex justify-between text-xs group/edit cursor-pointer"
                                  onDoubleClick={() => newPayRun.status === 'Draft' && setEditingField({ employeeId: item.employeeId, type: 'allowance', index: idx })}
                                >
                                  <span className="opacity-60">{a.name}</span>
                                  {editingField?.employeeId === item.employeeId && editingField.type === 'allowance' && editingField.index === idx ? (
                                    <input 
                                      autoFocus
                                      type="number"
                                      className="w-16 bg-white border border-[#141414]/20 rounded px-1 py-0.5 text-right font-bold"
                                      value={isNaN(a.amount) ? '' : (a.amount === 0 ? '' : a.amount)}
                                      onChange={(e) => handleItemChange(item.employeeId, 'allowance', e.target.value === '' ? 0 : parseFloat(e.target.value), idx)}
                                      onBlur={() => setEditingField(null)}
                                      onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold text-green-600">+{effectiveCurrency} {a.amount.toLocaleString()}</span>
                                      {newPayRun.status === 'Draft' && <Edit2 className="w-2 h-2 opacity-0 group-hover/edit:opacity-30" />}
                                    </div>
                                  )}
                                </div>
                              ))}
                              <div className="flex justify-between text-xs pt-1 border-t border-[#141414]/5 font-bold">
                                <span>Total</span>
                                <span>+{effectiveCurrency} {item.totalAllowances.toLocaleString()}</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[10px] opacity-30 italic">No allowances</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <p className="text-[10px] uppercase font-bold opacity-40 tracking-widest">Deductions</p>
                          {item.deductions.length > 0 ? (
                            <div className="space-y-1">
                              {item.deductions.map((d, idx) => (
                                <div 
                                  key={idx} 
                                  className="flex justify-between text-xs group/edit cursor-pointer"
                                  onDoubleClick={() => newPayRun.status === 'Draft' && setEditingField({ employeeId: item.employeeId, type: 'deduction', index: idx })}
                                >
                                  <span className="opacity-60">{d.name}</span>
                                  {editingField?.employeeId === item.employeeId && editingField.type === 'deduction' && editingField.index === idx ? (
                                    <input 
                                      autoFocus
                                      type="number"
                                      className="w-16 bg-white border border-[#141414]/20 rounded px-1 py-0.5 text-right font-bold"
                                      value={isNaN(d.amount) ? '' : (d.amount === 0 ? '' : d.amount)}
                                      onChange={(e) => handleItemChange(item.employeeId, 'deduction', e.target.value === '' ? 0 : parseFloat(e.target.value), idx)}
                                      onBlur={() => setEditingField(null)}
                                      onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold text-red-600">-{effectiveCurrency} {d.amount.toLocaleString()}</span>
                                      {newPayRun.status === 'Draft' && <Edit2 className="w-2 h-2 opacity-0 group-hover/edit:opacity-30" />}
                                    </div>
                                  )}
                                </div>
                              ))}
                              <div className="flex justify-between text-xs pt-1 border-t border-[#141414]/5 font-bold">
                                <span>Total</span>
                                <span>-{effectiveCurrency} {item.totalDeductions.toLocaleString()}</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[10px] opacity-30 italic">No deductions</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {newPayRun.items.length === 0 && (
                    <div className="text-center py-8 opacity-50 italic">
                      No active employees found for this organization.
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-8 border-t border-[#141414] flex items-center justify-end gap-4">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-colors"
                >
                  Cancel
                </button>
                {!isEditing && (
                  <button 
                    type="submit"
                    disabled={isSaving}
                    className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>Create Draft PayRun</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {isPaymentModalOpen && selectedPayRun && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPaymentModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-[32px] shadow-2xl border border-[#141414]">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between">
              <h2 className="text-lg font-bold italic serif">Record Payment</h2>
              <button 
                onClick={() => setIsPaymentModalOpen(false)}
                className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Date *</label>
                <input 
                  type="date" 
                  required
                  className="w-full bg-[#141414]/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-[#141414]/20 transition-all font-bold text-sm"
                  value={paymentData.date}
                  onChange={(e) => setPaymentData({...paymentData, date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Paid From *</label>
                  <select 
                    required
                    className="w-full bg-[#141414]/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-[#141414]/20 transition-all font-bold appearance-none text-sm"
                    value={paymentData.accountId}
                    onChange={(e) => {
                      const accountId = e.target.value;
                      const selectedAcc = paymentAccounts.find(a => a.id === accountId);
                      let method = 'Bank Transfer';
                      if (selectedAcc) {
                        if (selectedAcc.source === 'bank') {
                          method = 'Bank Transfer';
                        } else if (selectedAcc.name?.toLowerCase().includes('cash') || selectedAcc.accountName?.toLowerCase().includes('cash')) {
                          method = 'Cash';
                        } else if (selectedAcc.name?.toLowerCase().includes('credit card') || selectedAcc.accountName?.toLowerCase().includes('credit card')) {
                          method = 'Credit Card';
                        }
                      }
                      setPaymentData({...paymentData, accountId, method});
                    }}
                  >
                    <option value="">Select account...</option>
                    {paymentAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.source === 'bank' ? `Bank: ${acc.accountName || acc.name || 'Unnamed Bank'}` : `COA: ${acc.code} - ${acc.name}`}
                      </option>
                    ))}
                  </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Reference</label>
                <input 
                  type="text" 
                  placeholder="Optional reference number"
                  className="w-full bg-[#141414]/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-[#141414]/20 transition-all font-bold text-sm"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                />
              </div>
              <button 
                onClick={handleMarkAsPaid}
                disabled={isSaving}
                className="w-full bg-[#141414] text-[#E4E3E0] py-4 rounded-2xl font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Recording...
                  </>
                ) : (
                  'Record Payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDeletePayRun(deleteId)}
        title="Delete PayRun"
        message="Are you sure you want to delete this payrun? This action cannot be undone."
      />
    </div>
  );
}
